document.documentElement.classList.add("js");

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Only one audio or video plays at a time
let activeMedia = null;
function claimMedia(media) {
  if (activeMedia && activeMedia !== media) activeMedia.pause();
  activeMedia = media;
}

// Sticky nav background once the hero top leaves the viewport
(() => {
  const nav = document.querySelector("[data-nav]");
  const hero = document.querySelector(".hero");
  if (!nav || !hero) return;
  const sentinel = document.createElement("div");
  sentinel.style.cssText = "position:absolute;top:8px;height:1px;width:1px";
  hero.prepend(sentinel);
  new IntersectionObserver(([entry]) => {
    nav.classList.toggle("is-scrolled", !entry.isIntersecting);
  }).observe(sentinel);
})();

// Hero film loop: still frame under reduced motion, paused when off-screen
(() => {
  const v = document.querySelector("[data-hero-video]");
  if (!v) return;
  if (reduceMotion) { v.removeAttribute("autoplay"); v.pause(); return; }
  new IntersectionObserver(([e]) => { e.isIntersecting ? v.play().catch(() => {}) : v.pause(); }, { threshold: 0.05 }).observe(v);
  // Browsers only allow silent autoplay: one tap turns the film's sound on
  const btn = document.querySelector("[data-hero-sound]");
  if (!btn) return;
  const paint = () => {
    btn.setAttribute("aria-pressed", String(!v.muted));
    btn.innerHTML = v.muted
      ? '<i class="ph ph-speaker-slash" aria-hidden="true"></i><span>Tap anywhere for sound</span>'
      : '<i class="ph ph-speaker-high" aria-hidden="true"></i><span>Sound on</span>';
  };
  btn.addEventListener("click", () => {
    v.muted = !v.muted;
    if (!v.muted) { claimMedia(v); v.play().catch(() => {}); }
    paint();
  });
  v.addEventListener("pause", () => { if (!v.muted && activeMedia !== v) { v.muted = true; v.play().catch(() => {}); paint(); } });
  paint();

  // 1. Try real autoplay with sound (works only where the browser already trusts this site)
  const tryUnmuted = () => {
    if (!v.muted) return;
    v.muted = false;
    const p = v.play();
    if (p && p.catch) p.then(() => { claimMedia(v); paint(); }).catch(() => { v.muted = true; v.play().catch(() => {}); paint(); });
  };
  if (document.visibilityState === "visible") setTimeout(tryUnmuted, 300);

  // 2. Otherwise the first touch or click anywhere on the page turns the sound on (browsers count that as consent)
  const firstGesture = (e) => {
    if (e.target.closest && e.target.closest("[data-play], [data-video-play], a, button, input, label")) { off(); return; }
    if (v.muted) { v.muted = false; claimMedia(v); v.play().catch(() => { v.muted = true; }); paint(); }
    off();
  };
  const off = () => { ["pointerdown", "touchend", "keydown"].forEach((t) => document.removeEventListener(t, firstGesture, true)); };
  ["pointerdown", "touchend", "keydown"].forEach((t) => document.addEventListener(t, firstGesture, true));
})();

// Nav slides away while scrolling down and returns on the first scroll up (mobile media query applies the transform)
(() => {
  const nav = document.querySelector("[data-nav]");
  const menu = document.querySelector("[data-menu]");
  if (!nav) return;
  let last = window.scrollY;
  window.addEventListener("scroll", () => {
    const y = window.scrollY;
    if (menu && !menu.hidden) return;
    if (y > 120 && y > last + 4) nav.classList.add("is-hidden");
    else if (y < last - 4 || y <= 80) nav.classList.remove("is-hidden");
    last = y;
  }, { passive: true });
})();

// Mobile menu
(() => {
  const toggle = document.querySelector("[data-menu-toggle]");
  const menu = document.querySelector("[data-menu]");
  if (!toggle || !menu) return;
  const setOpen = (open) => {
    menu.hidden = !open;
    toggle.setAttribute("aria-expanded", String(open));
    toggle.querySelector("i").className = open ? "ph ph-x" : "ph ph-list";
    document.body.classList.toggle("menu-open", open);
    window.dispatchEvent(new Event("scroll"));
  };
  toggle.addEventListener("click", () => setOpen(menu.hidden));
  menu.addEventListener("click", (e) => { if (e.target.closest("a")) setOpen(false); });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") setOpen(false); });
})();

// Scroll reveal with per-group stagger
(() => {
  const items = document.querySelectorAll(".reveal");
  if (reduceMotion || !("IntersectionObserver" in window)) {
    items.forEach((el) => el.classList.add("is-in"));
    return;
  }
  const groups = new Map();
  items.forEach((el) => {
    const parent = el.parentElement;
    const i = groups.get(parent) ?? 0;
    el.style.setProperty("--stagger", `${i * 90}ms`);
    groups.set(parent, i + 1);
  });
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-in");
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: "0px 0px -10% 0px", threshold: 0.15 });
  items.forEach((el) => io.observe(el));
})();

// Waveform bars: deterministic heights, lit bars follow playback progress
function buildWave(el, bars = 40) {
  let seed = Number(el.dataset.seed) || 1;
  const rand = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280);
  const frag = document.createDocumentFragment();
  for (let i = 0; i < bars; i++) {
    const envelope = Math.sin((i / (bars - 1)) * Math.PI) * 0.6 + 0.4;
    const bar = document.createElement("i");
    bar.style.setProperty("--h", `${Math.max(18, Math.round(envelope * (40 + rand() * 60)))}%`);
    frag.appendChild(bar);
  }
  el.appendChild(frag);
}
document.querySelectorAll("[data-wave]").forEach((el) => buildWave(el));

// Audio players
(() => {
  const format = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  document.querySelectorAll("[data-player]").forEach((root) => {
    const btn = root.querySelector("[data-play]");
    const time = root.querySelector("[data-time]");
    const error = root.querySelector("[data-error]");
    const bars = [...root.querySelectorAll("[data-wave] i")];
    const icon = btn.querySelector("i");
    const label = btn.getAttribute("aria-label");
    const audio = new Audio();
    audio.preload = "none";

    const setPlaying = (playing) => {
      root.classList.toggle("is-playing", playing);
      icon.className = playing ? "ph-fill ph-pause" : "ph-fill ph-play";
      btn.setAttribute("aria-label", playing ? label.replace("Play", "Pause") : label);
    };

    audio.addEventListener("timeupdate", () => {
      if (!audio.duration) return;
      const lit = Math.round((audio.currentTime / audio.duration) * bars.length);
      bars.forEach((b, i) => b.classList.toggle("on", i < lit));
      time.textContent = format(audio.duration - audio.currentTime);
    });
    audio.addEventListener("play", () => setPlaying(true));
    audio.addEventListener("pause", () => setPlaying(false));
    audio.addEventListener("ended", () => bars.forEach((b) => b.classList.remove("on")));
    audio.addEventListener("error", () => { setPlaying(false); if (error) error.hidden = false; });

    btn.addEventListener("click", async () => {
      if (!audio.src) audio.src = root.dataset.src;
      if (audio.paused) {
        claimMedia(audio);
        try { await audio.play(); } catch { if (error) error.hidden = false; }
      } else {
        audio.pause();
      }
    });
  });
})();

// Reaction videos: click to play with sound, native controls once started
(() => {
  document.querySelectorAll("[data-video]").forEach((root) => {
    const video = root.querySelector("video");
    const btn = root.querySelector("[data-video-play]");
    video.addEventListener("play", () => { claimMedia(video); root.classList.add("is-playing"); });
    video.addEventListener("pause", () => root.classList.remove("is-playing"));
    video.addEventListener("ended", () => { root.classList.remove("is-playing"); video.controls = false; });
    btn.addEventListener("click", () => {
      video.controls = true;
      video.play().catch(() => { video.controls = true; });
    });
  });
})();

// Occasions rail arrows
(() => {
  const rail = document.querySelector("[data-rail]");
  const prev = document.querySelector("[data-rail-prev]");
  const next = document.querySelector("[data-rail-next]");
  if (!rail || !prev || !next) return;
  const step = () => rail.querySelector(".occ").getBoundingClientRect().width + 20;
  const update = () => {
    prev.disabled = rail.scrollLeft < 8;
    next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 8;
  };
  prev.addEventListener("click", () => rail.scrollBy({ left: -step(), behavior: reduceMotion ? "auto" : "smooth" }));
  next.addEventListener("click", () => rail.scrollBy({ left: step(), behavior: reduceMotion ? "auto" : "smooth" }));
  rail.addEventListener("scroll", update, { passive: true });
  update();
})();

// Reviews switcher
(() => {
  const tabs = [...document.querySelectorAll("[data-quote]")];
  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle("is-active", active);
        t.setAttribute("aria-selected", String(active));
        const q = document.getElementById(t.dataset.quote);
        q.hidden = !active;
        q.classList.toggle("is-active", active);
      });
    });
  });
})();

// Draft-only links
document.querySelectorAll("[data-cta], [data-todo]").forEach((a) => {
  if (a.getAttribute("href") === "#") a.addEventListener("click", (e) => e.preventDefault());
});

const year = document.querySelector("[data-year]");
if (year) year.textContent = new Date().getFullYear();

// Sticky "hear the first minute" bar once the hero has scrolled away (not while the menu is open)
(() => {
  const bar = document.querySelector("[data-stickycta]");
  const hero = document.querySelector(".hero");
  if (!bar || !hero) return;
  let on = false;
  const tick = () => {
    const mobile = window.innerWidth <= 640;
    const should = window.scrollY > (mobile ? Math.min(hero.offsetHeight - 80, 240) : hero.offsetHeight - 80) && !document.body.classList.contains("menu-open") && (document.documentElement.scrollHeight - window.innerHeight - window.scrollY) > 400;
    if (should === on) return;
    on = should;
    bar.classList.toggle("is-on", on);
    bar.setAttribute("aria-hidden", String(!on));
    bar.querySelector("a").tabIndex = on ? 0 : -1;
    document.body.classList.toggle("has-buybar", on);
  };
  window.addEventListener("scroll", tick, { passive: true });
  window.addEventListener("resize", tick);
  tick();
})();

// Message match with the ad: ?for=her | ?for=him rewrites their/them, remembered for the visit
(() => {
  const params = new URLSearchParams(location.search);
  let g = (params.get("for") || "").toLowerCase();
  try { if (g === "her" || g === "him") sessionStorage.setItem("odearly-for", g); else g = sessionStorage.getItem("odearly-for") || ""; } catch {}
  if (g !== "her" && g !== "him") return;
  const pron = g === "her" ? "her" : "his", obj = g === "her" ? "her" : "him";
  document.querySelectorAll("[data-pron]").forEach((el) => { el.textContent = pron; });
  document.querySelectorAll("[data-pron-cap]").forEach((el) => { el.textContent = pron[0].toUpperCase() + pron.slice(1); });
  document.querySelectorAll("[data-obj]").forEach((el) => { el.textContent = obj; });
  // Only the matching chips (Wife/Mom for her, Husband/Dad for him) plus the neutral ones
  document.querySelectorAll("[data-who-chip][data-g]").forEach((chip) => chip.classList.toggle("is-hidden", chip.dataset.g !== g));
  // Carry the hint into the questionnaire links
  document.querySelectorAll('a[href^="create.html"], a[href^="/pages/create"]').forEach((a) => { const u = new URL(a.getAttribute("href"), location.href); if (!u.searchParams.has("for")) u.searchParams.set("for", g); a.setAttribute("href", u.pathname.replace(/^\/index\.html$/, "/") + u.search); });
})();

// Launch-price countdown: ends at local midnight, every day
(() => {
  const el = document.querySelector("[data-launch-clock]");
  if (!el) return;
  const tick = () => {
    const now = new Date(), end = new Date(now); end.setHours(24, 0, 0, 0);
    const left = Math.max(0, end - now), h = Math.floor(left / 3600000), m = Math.floor((left % 3600000) / 60000), s = Math.floor((left % 60000) / 1000);
    el.textContent = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };
  tick(); setInterval(tick, 1000);
})();
